import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { NLPLogo } from '../components/nlp/NLPLogo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ArrowRight, Check, Sparkles, Users, GraduationCap, Building2 } from 'lucide-react';
import { useFoundingCohortCountdown } from '../hooks/useFoundingCohortCountdown';

const frameworkRows = [
  { stage: 'Profile', connects: 'Assessment data, personality insights, strengths, behavioural and communication tendencies', makes: 'Begin with a fuller understanding of the person—not only the problem they describe.' },
  { stage: 'Patterns', connects: 'Beliefs, conflicts, repeated themes, language, contradictions and practitioner observations', makes: 'See possible structures beneath the presenting issue and decide what deserves exploration.' },
  { stage: 'Progress', connects: 'Outcomes, milestones, actions, habits, reflections and check-ins', makes: 'Connect sessions to visible movement in everyday life.' },
  { stage: 'Pathways', connects: 'Intervention plans, learning resources, exercises, prompts and programme sequences', makes: 'Create a personalised route from present state to desired state.' },
  { stage: 'Presence', connects: 'Human practitioner skill, session history, Session Intelligence™ and between-session support', makes: 'Arrive better prepared, maintain continuity and keep the human relationship at the centre.' },
  { stage: 'Proof', connects: 'Reassessments, comparisons, goal movement, milestones and journey summaries', makes: 'Make progress easier to see, review and communicate.' },
  { stage: 'Promote', connects: 'Assessment-led lead generation, prospect journeys, content and conversion support', makes: 'Turn expertise into valuable conversations with the right prospective clients.' },
  { stage: 'Profit', connects: 'Offers, pricing, revenue visibility, capacity planning and an AI Business Coach', makes: 'Build the commercial structure that helps good practitioners remain in practice.' },
];

const journeySteps = [
  'Understand me before deciding what to do with me.',
  'Help me recognise the patterns influencing my current results.',
  'Clarify what I want and how we will know I am progressing.',
  'Build a pathway that fits me rather than applying a generic programme.',
  'Support implementation when I return to real life between sessions.',
  'Show me what has changed and how far I have come.',
];

const deliveryModels = [
  { model: 'Human-led coaching', how: 'You deliver the coaching directly. NLP OS supports profiling, preparation, notes, pathways, accountability and Proof.', value: 'Preserve the familiar one-to-one relationship while giving yourself better information and a more professional operating environment.' },
  { model: 'Hybrid one-to-one', how: 'Live human sessions are combined with client dashboards, actions, resources, AI micro-sessions and between-session support.', value: 'Create a more integrated experience in which the client remains connected to the work throughout the week—not only during the scheduled session.' },
  { model: 'Hybrid group coaching', how: 'Members attend live group sessions, then use individual pathways, dashboards, accountability and AI support between meetings.', value: 'Serve a group with shared teaching while each participant still receives a personal place to reflect, practise and progress.' },
  { model: 'Continuity membership', how: 'After a programme ends, clients can retain paid access to self-coaching tools, your frameworks, resources, goals, habits and AI support.', value: 'Give clients ongoing continuity while creating a monthly recurring-income stream beyond the original engagement.' },
];

const practitionerBenefits = [
  'Understand clients more deeply before choosing an intervention.',
  'Recognise possible patterns and leverage points earlier.',
  'Plan sessions with better continuity and less dependence on memory.',
  'Personalise pathways instead of starting every client journey from zero.',
  'Support reflection, habits and implementation between sessions.',
  'Track progress and create clearer evidence of value.',
  'Differentiate your service from generic coaching offers.',
  'Turn assessments and insight into meaningful prospect conversations.',
  'Build greater commercial visibility around pricing, capacity and revenue.',
  'Develop one-to-one, hybrid, group and continuity offers with the AI Business Coach.',
  'Add paid platform access or membership options that can create recurring income.',
];

const trainerBenefits = [
  'Build repeatable pathways around your models, exercises and resources.',
  'Combine automation with personalisation rather than choosing one or the other.',
  'Give participants structured actions, reflections and implementation support.',
  'Use programme knowledge through AI-supported chat or voice experiences.',
  'Track engagement, milestones and outcomes across the journey.',
  'Create a more valuable and differentiated programme experience.',
  'Extend programme value through ongoing paid access after the live delivery ends.',
];

const schoolBenefits = [
  'a stronger and more modern certification proposition;',
  'a practical bridge from training-room knowledge to real client work;',
  'responsible AI support that keeps classical NLP and human judgment central;',
  'greater student confidence after graduation;',
  'ongoing practitioner development and alumni engagement;',
  'a clearer way to support graduate implementation and client outcomes;',
  'differentiation from schools that teach techniques but provide no professional infrastructure;',
  'potential recurring value through platform participation or licensing arrangements where available.',
];

const faqs = [
  {
    question: 'Is NLP OS only for NLP practitioners?',
    answer: 'No. NLP OS is rooted in NLP and designed strongly around the needs of NLP practitioners and schools, but professionally trained coaches, trainers and change practitioners can use the same operating architecture around their own frameworks and methodology.',
  },
  {
    question: 'Will AI replace my role with the client?',
    answer: 'No. NLP OS follows a human-led, AI-supported model. AI can organise information and surface possibilities. You remain responsible for context, rapport, professional judgment, ethics and intervention.',
  },
  {
    question: 'Does the system diagnose clients?',
    answer: 'No. Session and pattern analysis is designed to offer careful coaching hypotheses and areas for practitioner consideration, not clinical labels or definitive conclusions.',
  },
  {
    question: 'Can NLP OS support clients between sessions?',
    answer: 'Yes. Depending on your configuration, clients can work with goals, habits, reflections, resources, pathways and AI-supported chat or voice experiences between live interactions.',
  },
  {
    question: 'Can I use my own methodology and resources?',
    answer: 'Pathways is designed to help practitioners and training organisations organise their own processes, exercises, learning resources and programme sequences inside a more connected client journey.',
  },
  {
    question: 'How can NLP OS help me attract clients?',
    answer: 'Promote can support assessment-led lead generation, prospect insight, discovery pathways, client education and proof-driven communication. The aim is to begin with value and understanding rather than a generic sales message.',
  },
  {
    question: 'Can I create recurring income with NLP OS?',
    answer: 'Yes. A practitioner can include platform access within a hybrid programme, build a recurring group-coaching membership or offer paid continuity access after the main engagement. Schools can also explore monthly or annual practitioner access after certification. The exact commercial options depend on the plan or licensing arrangement selected.',
  },
  {
    question: 'Can the AI experience use my frameworks or voice?',
    answer: 'The AI experience can be informed by approved practitioner or school frameworks, resources and programme knowledge. Branded experiences and practitioner voice options depend on the configuration and feature availability agreed for the account.',
  },
  {
    question: 'How can this work for a certification school?',
    answer: 'A school can use NLP OS to extend support beyond the training event, give graduates practical infrastructure, connect methodology to real client application and strengthen ongoing practitioner development. School licensing and participation arrangements can be discussed directly.',
  },
  {
    question: 'Is client permission required for session analysis?',
    answer: 'Yes. Practitioners should obtain appropriate permission before uploading or analysing client session material and follow the professional, contractual and data-protection requirements that apply to their work.',
  },
  {
    question: "What's the difference between the monthly, annual and lifetime offers?",
    answer: 'Core Monthly ($99/month) gives you one coach account with up to 25 active clients and the Core platform tools. Unlimited Growth OS Annual ($1,990/year) adds unlimited clients, the Growth OS tools, private onboarding, the Profile Intensive, a two-day workshop and training recordings. Founder Lifetime ($5,000 once, currently unavailable) adds lifetime access, white-label branding, and private half-day intensives with Patrick and Shelly.',
  },
  {
    question: 'Is AI usage included?',
    answer: 'AI can be built into the price of the coaching programmes and client experiences you sell. When you set your client fees to cover the AI features they use, your clients’ payments cover that usage while you keep the margin you’ve planned. AI usage is billed separately through the provider account you connect, so you can see the actual cost and price your offers accordingly. Your NLP OS fee covers access to the platform.',
  },
];

const scrollToOffers = () => {
  document.getElementById('founding-cohort-offers')?.scrollIntoView({ behavior: 'smooth' });
};

const scrollToSchools = () => {
  document.getElementById('schools')?.scrollIntoView({ behavior: 'smooth' });
};

export function NLPSalesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { timeLeft, pad } = useFoundingCohortCountdown();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen bg-nlp-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-nlp-bg/95 backdrop-blur-lg shadow-sm">
        <div className="max-w-[1216px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/home')} className="flex items-center">
              <NLPLogo className="h-12" />
            </button>
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToOffers()} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Offers
              </button>
              <button onClick={() => scrollToSchools()} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Schools
              </button>
              <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                FAQ
              </button>
            </nav>
            <div className="flex items-center gap-3">
              <Button
                onClick={scrollToOffers}
                className="bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full opacity-60"
              >
                Offers Closed
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Offer Closed Banner */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-nlp-forest text-nlp-bg py-4 px-4 shadow-md">
        <div className="max-w-[1216px] mx-auto flex items-center justify-center gap-3 text-base md:text-lg font-semibold">
          <span className="text-nlp-gold-light">Founding Cohort Offer is Now Closed</span>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 px-4 md:px-8 bg-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-olive/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-nlp-gold/5 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="max-w-[900px] mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-nlp-gold-dark" />
            <span className="text-sm font-semibold text-nlp-forest">Classical NLP. Modern Architecture. AI-Powered Application.</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-nlp-forest mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Turn Your NLP & Coaching Expertise Into a Complete Client Transformation System
          </h1>
          <p className="text-lg md:text-xl text-nlp-taupe mb-8 max-w-2xl mx-auto leading-relaxed">
            NLP OS<sup className="text-[0.45em] align-super ml-0.5">™</sup> brings client understanding, pattern recognition, personalised pathways, practitioner intelligence, progress tracking, proof of transformation and practice growth into one connected operating system. So you can do more than deliver a powerful session. You can create a professional transformation journey before, during and between sessions—and build a practice capable of sustaining that work.
          </p>
          <p className="text-base text-nlp-taupe mb-8 max-w-2xl mx-auto">
            Built for NLP practitioners and schools—and equally relevant to coaches, trainers and change professionals who want a more connected, AI-supported way to deliver and grow their work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={scrollToOffers} className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full opacity-60">
              Founding Cohort Closed
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button variant="secondary" size="lg" onClick={scrollToSchools} className="gap-2 bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full">
              Explore NLP OS for Schools
            </Button>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            NLP Has Never Lacked Powerful Techniques
          </h2>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>The real challenge is everything that has to happen around the technique.</p>
            <p>Who is the person behind the presenting problem? What patterns may be maintaining the current result? What should you explore first? Which pathway fits this client? What happens when the client returns to their everyday environment? Is progress actually happening? How do you show the value of the work? And how do you turn professional skill into a sustainable practice?</p>
            <p>Too often, those questions are handled through memory, intuition, handwritten notes, spreadsheets, disconnected apps—or not at all.</p>
            <p className="text-nlp-forest font-semibold text-xl pt-2">NLP OS provides the architecture that connects them.</p>
          </div>
        </div>
      </section>

      {/* 8P Framework */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[1216px] mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <span className="text-sm font-semibold text-nlp-forest">The 8P Framework™</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              One Operating System Around the Whole Transformation Journey
            </h2>
            <p className="text-lg text-nlp-taupe max-w-2xl mx-auto">
              Most platforms solve one isolated part of the practitioner's work. NLP OS is built around a different question: What does meaningful transformation require from the first interaction through to the final outcome?
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-nlp-cream-dark">
                  <th className="text-left py-4 px-4 text-nlp-forest font-bold text-lg">Stage</th>
                  <th className="text-left py-4 px-4 text-nlp-forest font-bold text-lg">What NLP OS connects</th>
                  <th className="text-left py-4 px-4 text-nlp-forest font-bold text-lg">What this makes possible</th>
                </tr>
              </thead>
              <tbody>
                {frameworkRows.map((row) => (
                  <tr key={row.stage} className="border-b border-nlp-cream-dark hover:bg-nlp-cream/30 transition-colors">
                    <td className="py-4 px-4 font-semibold text-nlp-forest whitespace-nowrap">{row.stage}</td>
                    <td className="py-4 px-4 text-nlp-taupe">{row.connects}</td>
                    <td className="py-4 px-4 text-nlp-taupe">{row.makes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Connected Journey */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            From a Powerful Session to a Connected Transformation Journey
          </h2>
          <p className="text-lg text-nlp-taupe mb-8 leading-relaxed">
            Your client should not have to experience coaching as a series of isolated conversations. With NLP OS, the journey can become:
          </p>
          <ul className="space-y-3">
            {journeySteps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-nlp-forest text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <span className="text-lg text-nlp-forest">{step}</span>
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-taupe mt-8 leading-relaxed">
            For the practitioner, that means better information, better preparation, better continuity, clearer evidence and a more professional client experience.
          </p>
        </div>
      </section>

      {/* Profile */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Understand the Person Before Trying to Change the Problem
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            A presenting problem is only one part of the person sitting in front of you. Profile gives you a multidimensional starting point through client assessments, 12-characteristic profile insights, behavioural and communication tendencies, motivational patterns, strengths, developmental opportunities, Harmonic levels and AI-supported narrative interpretation.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            The purpose is not to type or label the client. It is to inform your attention. Before the first substantial session, you can begin asking better questions:
          </p>
          <ul className="space-y-2 mb-4">
            {[
              'How might this person prefer to communicate and make decisions?',
              'Which strengths could become leverage points for change?',
              'Where might recurring challenges or blind spots be worth exploring?',
              'How could I personalise the conversation from the beginning?',
            ].map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {q}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-forest font-medium leading-relaxed">
            Instead of beginning with 'Which technique should I use?' you begin with 'Who is this person, how are they currently operating, and what do I need to understand before deciding what to do?'
          </p>
        </div>
      </section>

      {/* Patterns */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Move From Technique First to Pattern Informed
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Clients rarely arrive with a precise description of the unconscious structure influencing their results. They arrive with stories, symptoms, frustrations and desired outcomes.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Patterns brings together information from profiles, assessments, client language, practitioner notes and sessions to surface possible beliefs, identity statements, values conflicts, internal conflicts, emotional themes, repeated loops, contradictions and leverage points.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            This does not hand professional judgment to an algorithm. It gives the practitioner more organised information to consider.
          </p>
          <p className="text-lg text-nlp-forest font-medium leading-relaxed">
            The shift is simple but important: From 'Which familiar technique can I apply?' to 'What am I seeing, what might it suggest, and what should I explore next?'
          </p>
        </div>
      </section>

      {/* Session Intelligence */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            A Second Set of Eyes Around Every Session
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            A client can reveal beliefs, identity statements, internal conflict, emotional shifts and significant language in subtle ways. After the session, most practitioners are left with memory, notes, intuition and a general sense of what happened.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Session Intelligence™ helps you go one step further. Where enabled—and with the client's permission—you can analyse session audio, a transcript or your notes and receive a structured practitioner insight report. It can surface:
          </p>
          <ul className="space-y-2 mb-4">
            {[
              'significant client language and possible Meta Model opportunities;',
              'potential beliefs and identity statements;',
              'emotional themes and repeated patterns;',
              'contradictions or internal conflicts worth noticing;',
              'possible follow-up questions and areas for the next session.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            The analysis is not a diagnosis and does not tell you what is definitively true about the client. It offers careful coaching hypotheses for you to evaluate.
          </p>
          <p className="text-lg text-nlp-forest font-medium leading-relaxed">
            AI provides possibilities. You provide context, rapport, ethics, judgment and the human intervention.
          </p>
        </div>
      </section>

      {/* Progress & Pathways */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Change Should Not End When the Session Ends
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            A breakthrough in the coaching room still has to survive contact with the client's everyday environment. Progress and Pathways connect insight to implementation. Together, they allow you to clarify outcomes, establish meaningful measures, assign relevant resources, build habits, track actions, collect reflections and maintain momentum between interactions.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Your clients can receive a clearer answer to 'What happens next?' You can see where progress is occurring, where implementation has stalled and what deserves attention in the next conversation—without rebuilding the whole context from memory.
          </p>
          <p className="text-lg text-nlp-taupe leading-relaxed">
            AI chat and voice support can also help clients reflect, revisit assigned questions, access programme knowledge and remain engaged between live sessions. It is designed to complement your professional support, not replace it.
          </p>
        </div>
      </section>

      {/* Proof */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Make Transformation Visible
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Meaningful change is often real but difficult to demonstrate. Clients forget where they began. Practitioners remember that important work occurred but lack a structured view of the journey.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Proof connects reassessments, profile comparisons, goal movement, habits, milestones, client reflections and practitioner observations so progress becomes easier to see. That can help you:
          </p>
          <ul className="space-y-2 mb-4">
            {[
              'review the client journey more intelligently;',
              'reinforce progress the client may have overlooked;',
              'improve your own delivery over time;',
              'support renewal and continuation conversations appropriately;',
              'develop testimonials or case studies with permission;',
              'show the value of a structured longer-term engagement.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-forest font-medium leading-relaxed">
            Transformation should not disappear when the conversation ends.
          </p>
        </div>
      </section>

      {/* Promote */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Promote: Turn Expertise Into Valuable Client Conversations
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Being highly skilled does not automatically create a reliable flow of clients. Promote helps connect your professional capability to the people who could benefit from it.
          </p>
          <p className="text-lg text-nlp-forest font-medium mb-4 leading-relaxed">
            Assessment → Personalised Insight → AI-Supported Experience → Coaching Conversation → Client
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Promote can help you use assessments, diagnostic experiences, prospect profiles, educational content, AI coach interactions and Proof from existing client journeys to create more relevant marketing and more meaningful discovery conversations. This can support you to:
          </p>
          <ul className="space-y-2 mb-4">
            {[
              'use assessments and AI experiences as lead-generation tools;',
              'give prospective clients useful insight before asking for a larger commitment;',
              "personalise follow-up around the prospect's stated goals and patterns;",
              'develop clearer positioning, content and offers;',
              'move naturally from insight into a professional coaching conversation;',
              'use verified client Proof to strengthen authority and trust.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-taupe leading-relaxed">
            Promote is not simply about posting more content. It is about building a connected path from first interest to an informed decision to work with you.
          </p>
        </div>
      </section>

      {/* Profit */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Profit: Build the Offers, Numbers and Business Behind the Practice
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Profit gives you a place to think about the commercial model that makes your work sustainable. Through the Profit Centre and AI Business Coach, you can explore and develop offers, pricing, programme structures, capacity, revenue targets and recurring-income models.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            For example, you can explore questions such as:
          </p>
          <ul className="space-y-2 mb-4">
            {[
              'What should be included in my one-to-one, group or hybrid offer?',
              'How many clients or members would I need at a particular price?',
              'What could the business look like if part of my income became recurring?',
              'How do different conversion rates or programme structures affect the forecast?',
              'How much delivery capacity do I have before the client experience suffers?',
              'Which offer creates the best balance between impact, access and commercial sustainability?',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-taupe leading-relaxed">
            The purpose of Profit is not to turn coaching into a spreadsheet. It is to help capable practitioners build a business that allows them to continue doing meaningful work.
          </p>
        </div>
      </section>

      {/* Delivery Models */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[1216px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              More Than One Way to Deliver and Earn
            </h2>
            <p className="text-lg text-nlp-taupe max-w-2xl mx-auto">
              NLP OS does not force every practitioner into the same delivery model. It gives you an infrastructure that can support fully human-led coaching, hybrid delivery, group programmes and ongoing self-coaching access.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-nlp-cream-dark">
                  <th className="text-left py-4 px-4 text-nlp-forest font-bold">Delivery model</th>
                  <th className="text-left py-4 px-4 text-nlp-forest font-bold">How it can work</th>
                  <th className="text-left py-4 px-4 text-nlp-forest font-bold">Commercial and client value</th>
                </tr>
              </thead>
              <tbody>
                {deliveryModels.map((row) => (
                  <tr key={row.model} className="border-b border-nlp-cream-dark hover:bg-nlp-cream/30 transition-colors">
                    <td className="py-4 px-4 font-semibold text-nlp-forest whitespace-nowrap">{row.model}</td>
                    <td className="py-4 px-4 text-nlp-taupe">{row.how}</td>
                    <td className="py-4 px-4 text-nlp-taupe">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Hybrid Experience */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            The Hybrid Client Experience
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            In a hybrid programme, the practitioner remains the leader of the transformation. NLP OS extends the relationship around the live work.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Between sessions, the client can return to a personal dashboard to review outcomes, complete actions, track habits, journal, explore assigned pathways and receive AI-supported micro-sessions when appropriate. The AI experience can be informed by the practitioner's frameworks and, where configured, delivered through a branded voice or the practitioner's own approved voice experience.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            This gives the client an ongoing connection point when a question, challenge or opportunity appears in real life. Instead of waiting until the next appointment—or losing the insight altogether—they can reflect, take a next step and bring richer information back to the human session.
          </p>
          <p className="text-lg text-nlp-forest font-medium leading-relaxed">
            The result is not less human coaching. It is a more continuous and integrated coaching environment around the human relationship.
          </p>
        </div>
      </section>

      {/* Recurring Income */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Build Recurring Income Without Abandoning Human-Led Work
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Most coaching and training businesses are transactional. A practitioner delivers a session or programme, receives payment, completes the work and then begins again from zero.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            NLP OS creates the possibility of a different commercial foundation. You can continue charging for high-value human coaching while including platform access inside the programme fee. You can build a monthly hybrid group where members receive live coaching and use NLP OS throughout the week. Or, after a client completes an intensive programme, you can offer an ongoing paid continuity plan for self-coaching, accountability, resources and AI support.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Clients are already paying for AI coaching experiences. NLP OS allows the experience to be connected to your methodology, your resources, your programme and—where enabled—your brand and voice, rather than sending the client to a generic tool with no continuity from the work you completed together.
          </p>
          <p className="text-lg text-nlp-taupe leading-relaxed">
            Over time, recurring subscriptions can create a more stable base beneath one-to-one work, group programmes and certification income. That does not guarantee revenue, but it gives practitioners a business model capable of compounding rather than resetting after every engagement.
          </p>
        </div>
      </section>

      {/* Reduce Fragmentation */}
      <section className="py-20 px-4 md:px-8 bg-nlp-forest relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-nlp-gold/10 rounded-full blur-3xl" />
        <div className="max-w-[800px] mx-auto relative">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-bg mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            You Became a Practitioner to Create Change, Not Manage Seven Disconnected Systems
          </h2>
          <p className="text-lg text-nlp-cream mb-8 leading-relaxed">
            NLP OS is designed to reduce the fragmentation around professional practice.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              'Prepare with a richer understanding of the client.',
              'Keep profiles, patterns, goals, pathways and session intelligence connected.',
              'Organise resources and personalised client journeys in one professional environment.',
              'See what is happening between sessions without relying only on the next conversation.',
              'Use evidence from the journey to demonstrate progress and improve your work.',
              'Connect client delivery with lead generation, offers and business growth.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-nlp-cream">
                <Check className="w-5 h-5 text-nlp-gold-light flex-shrink-0 mt-0.5" />
                <span className="text-lg">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-xl text-nlp-gold-light font-semibold mb-8">Everything around the intervention—connected.</p>
          <Button size="lg" onClick={scrollToOffers} className="gap-2 bg-nlp-gold text-nlp-forest hover:bg-nlp-gold-light rounded-full opacity-60">
            Founding Cohort Closed
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* For Coaches and NLP Practitioners */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-nlp-olive" />
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>
              For Coaches and NLP Practitioners
            </h2>
          </div>
          <p className="text-lg text-nlp-taupe mb-6 leading-relaxed">
            Apply what you already know with greater structure, continuity and professional support.
          </p>
          <ul className="space-y-2">
            {practitionerBenefits.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-nlp-taupe">
                <Check className="w-5 h-5 text-nlp-olive flex-shrink-0 mt-0.5" />
                <span className="text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* For Trainers */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap className="w-6 h-6 text-nlp-olive" />
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>
              For Trainers and Programme Creators
            </h2>
          </div>
          <p className="text-lg text-nlp-taupe mb-6 leading-relaxed">
            Turn your expertise into an environment clients can use—not only information they consume.
          </p>
          <ul className="space-y-2">
            {trainerBenefits.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-nlp-taupe">
                <Check className="w-5 h-5 text-nlp-olive flex-shrink-0 mt-0.5" />
                <span className="text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* For Coaches Beyond NLP */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            For Coaches Beyond NLP
          </h2>
          <p className="text-lg text-nlp-taupe leading-relaxed">
            Although the platform is called NLP OS, the operating architecture is not limited to practitioners who use NLP exclusively. A professionally trained coach, trainer or change practitioner can use Profile, Progress, Pathways, Presence, Proof, Promote and Profit around their own methodology. They can add their frameworks, learning resources, exercises, questions and programme structure while using NLP OS as the delivery and business environment around the work.
          </p>
          <p className="text-lg text-nlp-taupe mt-4 leading-relaxed">
            NLP provides an important foundation and language inside the platform. It does not prevent other credible coaching and change approaches from using the same connected architecture.
          </p>
        </div>
      </section>

      {/* === FOUNDING COHORT OFFERS === */}
      <section id="founding-cohort-offers" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-cream scroll-mt-20">
        <div className="max-w-[1216px] mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full mb-4 border border-red-200">
              <span className="text-sm font-bold text-red-700 uppercase tracking-wide">Offer Closed</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Founding Cohort Offers Are Now Closed
            </h2>
            <p className="text-lg text-nlp-taupe max-w-2xl mx-auto leading-relaxed">
              Thank you to everyone who joined the founding cohort. These offers are no longer available. Please contact us to learn about future opportunities to work with NLP OS.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Core Monthly */}
            <div className="bg-white rounded-3xl border border-nlp-cream-dark p-8 flex flex-col hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-nlp-forest mb-1">Core Monthly</h3>
              <div className="mb-1">
                <span className="text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>$99</span>
                <span className="text-lg text-nlp-taupe">/month</span>
              </div>
              <p className="text-sm text-nlp-taupe mb-6 font-medium">Start using NLP OS with your clients.</p>
              <ul className="space-y-3 flex-1">
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm">One coach account</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Up to <strong>25 active clients/respondents</strong> as a founding cohort member (usually 10)</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Core tools for assessments, profile insights, AI-supported analysis, and client tracking</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-nlp-cream-dark">
                <p className="text-sm text-nlp-taupe mb-4">
                  Want personal help getting started? Add our <strong>Private Implementation Package for $299 once</strong>:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-nlp-taupe">
                    <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                    <span className="text-sm">A private platform onboarding session with Shelly to get you set up and comfortable using NLP OS.</span>
                  </li>
                  <li className="flex items-start gap-2 text-nlp-taupe">
                    <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                    <span className="text-sm">A private <strong>NLP OS Practice Monetisation Session</strong> with Patrick to shape an offer, decide how you'll use the platform with paying clients, and identify your next steps.</span>
                  </li>
                </ul>
              </div>
              <Button
                size="lg"
                disabled
                className="w-full bg-gray-300 text-gray-500 rounded-full mt-auto cursor-not-allowed"
              >
                Closed
              </Button>
            </div>

            {/* Unlimited Growth OS Annual */}
            <div className="bg-white rounded-3xl border-2 border-nlp-olive p-8 flex flex-col hover:shadow-lg transition-shadow relative">
              <h3 className="text-xl font-bold text-nlp-forest mb-1">Unlimited Growth OS Annual</h3>
              <div className="mb-1">
                <span className="text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>$1,990</span>
                <span className="text-lg text-nlp-taupe">/year</span>
              </div>
              <p className="text-sm text-nlp-taupe mb-6 font-medium">Build your client practice with unlimited room to grow.</p>
              <ul className="space-y-3 flex-1">
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm">One coach account with the Growth OS tools for client acquisition, follow-up, and growth</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Unlimited active clients/respondents</strong> as a founding cohort member</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Private onboarding with Shelly:</strong> Get set up, find your way around the platform and learning materials, and start using NLP OS in your practice.</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Private NLP OS Practice Monetisation Session with Patrick:</strong> Work through your offer, how you'll use the platform with paying clients, and a practical plan to take it to market.</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>NLP OS Profile Intensive and manual:</strong> Go deeper into the characteristics profile, the harmonic scale profile, how they interact, and how to apply the insights with clients.</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Two-day NLP OS Workshop:</strong> Work through the platform and see how its elements connect across the client journey.</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-taupe">
                  <Check className="w-4 h-4 text-nlp-olive flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Training recordings:</strong> Join the trainings live or learn from the recordings.</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-nlp-cream-dark">
                <p className="text-sm text-nlp-forest font-medium">
                  Your $1,990 annual price is locked in at renewal while you keep your subscription active.
                </p>
              </div>
              <Button
                size="lg"
                disabled
                className="w-full bg-gray-300 text-gray-500 rounded-full mt-4 cursor-not-allowed"
              >
                Closed
              </Button>
            </div>

            {/* Founder Lifetime */}
            <div className="bg-nlp-forest rounded-3xl p-8 flex flex-col hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-nlp-gold text-nlp-forest text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                Best Value
              </div>
              <h3 className="text-xl font-bold text-nlp-bg mb-1">Founder Lifetime</h3>
              <p className="text-xs text-nlp-gold-light font-semibold mb-1">
                <span>0 Places Available</span>
              </p>
              <div className="mb-1">
                <span className="text-4xl font-bold text-nlp-bg" style={{ fontFamily: 'Georgia, serif' }}>$5,000</span>
                <span className="text-lg text-nlp-cream"> once</span>
              </div>
              <p className="text-sm text-nlp-cream mb-6 font-medium">Make NLP OS a lasting part of your own coaching practice, with dedicated time to build around it.</p>
              <ul className="space-y-3 flex-1">
                <li className="flex items-start gap-2 text-nlp-cream">
                  <Check className="w-4 h-4 text-nlp-gold-light flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Lifetime platform access for <strong>one coach account</strong> with unlimited active clients/respondents</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-cream">
                  <Check className="w-4 h-4 text-nlp-gold-light flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Profile Intensive, its manual, the two-day NLP OS Workshop, and the training recordings</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-cream">
                  <Check className="w-4 h-4 text-nlp-gold-light flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Your own branding:</strong> Present your practice through a white label setup with your logo and custom domain.</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-cream">
                  <Check className="w-4 h-4 text-nlp-gold-light flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>A private half-day Business Breakthrough Intensive with Patrick:</strong> Work through the path from attracting attention and creating your offer to enrolling clients and delivering their journey through NLP OS. Leave with clear priorities for putting the plan into action.</span>
                </li>
                <li className="flex items-start gap-2 text-nlp-cream">
                  <Check className="w-4 h-4 text-nlp-gold-light flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>A private half-day platform implementation session with Shelly:</strong> Set up the platform for your practice and learn how to use the features that support your client journey.</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-nlp-forest-light">
                <p className="text-sm text-nlp-cream">
                  Founder Lifetime is for <strong>your own coaching practice</strong>. It does not include accounts for other coaches or rights to resell NLP OS. Trainer and Partner arrangements are available separately.
                </p>
              </div>
              <Button
                size="lg"
                disabled
                className="w-full bg-gray-300 text-gray-500 rounded-full mt-4 cursor-not-allowed"
              >
                Closed
              </Button>
            </div>
          </div>

          {/* AI Usage Note */}
          <div className="mt-12 max-w-[800px] mx-auto bg-white rounded-2xl border border-nlp-cream-dark p-6">
            <h3 className="text-lg font-bold text-nlp-forest mb-2">How does AI usage work?</h3>
            <p className="text-base text-nlp-taupe leading-relaxed">
              AI can be built into the price of the coaching programmes and client experiences you sell. When you set your client fees to cover the AI features they use, your clients’ payments cover that usage while you keep the margin you’ve planned. AI usage is billed separately through the provider account you connect, so you can see the actual cost and price your offers accordingly. Your NLP OS fee covers access to the platform.
            </p>
          </div>

          {/* Ready CTA */}
          <div className="mt-12 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Founding Cohort Offers Are Now Closed
            </h3>
            <p className="text-lg text-nlp-taupe mb-6">
              Thank you to everyone who joined the founding cohort. Please contact us to learn about future opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* For NLP Certification Schools */}
      <section id="schools" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg scroll-mt-20">
        <div className="max-w-[800px] mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-nlp-olive" />
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>
              For NLP Certification Schools
            </h2>
          </div>
          <p className="text-lg text-nlp-taupe mb-6 leading-relaxed">
            Give your students more than a certification. Give them the operating system to apply what you teach.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Traditional NLP education usually follows a familiar path: Learn → Practise → Assess → Certify. Then the graduate leaves the training room and faces an entirely different set of questions:
          </p>
          <ul className="space-y-2 mb-6">
            {[
              'How do I begin with a real client?',
              'How do I decide what to work on first?',
              'How do I structure a complete client journey?',
              'How do I know whether progress is happening?',
              'How do I get clients and build a professional practice?',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-forest font-medium mb-4 leading-relaxed">
            NLP OS helps close the gap between knowing NLP and becoming a practising professional.
          </p>

          <h3 className="text-2xl font-bold text-nlp-forest mb-4 mt-8" style={{ fontFamily: 'Georgia, serif' }}>
            Move From Teaching NLP to Developing NLP Professionals
          </h3>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            With NLP OS, the practitioner-development journey can continue: Learn → Practise → Certify → Apply → Support → Measure → Improve → Build
          </p>
          <p className="text-lg text-nlp-taupe mb-6 leading-relaxed">
            Your graduates can leave with more than a manual and a certificate. They can leave with an environment designed to help them assess clients, consider patterns, plan the work, support implementation, track progress, demonstrate outcomes and begin building a practice.
          </p>
          <p className="text-lg text-nlp-taupe mb-2 leading-relaxed">For the school, this can create:</p>
          <ul className="space-y-2 mb-8">
            {schoolBenefits.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-nlp-taupe">
                <span className="text-nlp-olive font-bold flex-shrink-0">•</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-forest font-medium mb-8 leading-relaxed">
            Your school stops being only the place where students learned NLP. It can become the ecosystem in which they develop as professionals.
          </p>

          <h3 className="text-2xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            A Recurring Revenue Model for Schools
          </h3>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            Most training schools are also transactional. They enrol a cohort, deliver the certification and then need to sell the next event to generate the next cycle of income. NLP OS creates the possibility of an ongoing platform relationship with every graduate.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            A school can include initial platform access within certification and keep graduates connected through implementation support and continuing development. It can then offer continued access as a monthly or annual professional-practice subscription, including premium pathways or AI experiences based on its own methodology.
          </p>
          <p className="text-lg text-nlp-taupe mb-8 leading-relaxed">
            Each new cohort can therefore add to an expanding base of active practitioners rather than disappearing from the school's commercial ecosystem after graduation. The school gains the possibility of recurring platform income while students gain a practical reason to remain engaged.
          </p>
          <p className="text-lg text-nlp-taupe mb-8 leading-relaxed">
            For coaching schools, the same model applies. The architecture can support the school's own coaching frameworks and delivery model even when the programme is not primarily NLP-based.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/coach-registration')}
            className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
          >
            Discuss NLP OS for Your School
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Human-Led AI-Supported */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-4 border border-nlp-cream-dark">
            <span className="text-sm font-semibold text-nlp-forest">Human-Led, AI-Supported</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Human-Led, AI-Supported
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            NLP OS is not designed to replace the practitioner. It is designed to make the practitioner better equipped.
          </p>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            AI can help organise information, recognise possible patterns, prepare sessions, analyse conversations, generate possibilities, maintain continuity and support structured client reflection. But context, sensory acuity, rapport, professional judgment, intuition, ethics and intervention remain human responsibilities.
          </p>
          <p className="text-lg text-nlp-forest font-medium leading-relaxed">
            The practitioner stays at the centre of the 8P Framework because technology should increase human presence—not remove it.
          </p>
        </div>
      </section>

      {/* The New Model of NLP */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            The New Model of NLP
          </h2>
          <p className="text-lg text-nlp-taupe mb-6 leading-relaxed">
            NLP OS brings three layers together:
          </p>
          <div className="space-y-4">
            <div className="bg-nlp-cream/50 rounded-2xl p-6 border border-nlp-cream-dark">
              <h3 className="font-bold text-nlp-forest mb-2">1. Classical NLP</h3>
              <p className="text-nlp-taupe">The foundational models, principles, practitioner skills and change processes that made NLP powerful.</p>
            </div>
            <div className="bg-nlp-cream/50 rounded-2xl p-6 border border-nlp-cream-dark">
              <h3 className="font-bold text-nlp-forest mb-2">2. Modern Architecture</h3>
              <p className="text-nlp-taupe">The 8P Framework provides a repeatable operating model around the complete transformation journey.</p>
            </div>
            <div className="bg-nlp-cream/50 rounded-2xl p-6 border border-nlp-cream-dark">
              <h3 className="font-bold text-nlp-forest mb-2">3. AI-Powered Application</h3>
              <p className="text-nlp-taupe">NLP OS brings the architecture to life through assessments, practitioner intelligence, client tools, progress systems, reporting and practice-growth support.</p>
            </div>
          </div>
          <p className="text-lg text-nlp-forest font-medium mt-6 leading-relaxed">
            Classical NLP provides the methodology. The 8P Framework provides the architecture. NLP OS provides the technology. AI provides intelligent augmentation. The practitioner provides the humanity.
          </p>
        </div>
      </section>

      {/* What NLP OS Is Not */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            What NLP OS Is Not
          </h2>
          <p className="text-lg text-nlp-taupe mb-4 leading-relaxed">
            NLP OS is not a shortcut that turns an untrained person into a practitioner. It is not a replacement for professional training, ethics or judgment. It is not simply an AI note taker, CRM, assessment, habit tracker or marketing tool.
          </p>
          <p className="text-lg text-nlp-taupe leading-relaxed">
            Those functions may all contribute to the system. The difference is that they operate inside one connected architecture built around the transformation journey.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg scroll-mt-20">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <span className="text-sm font-semibold text-nlp-forest">FAQ</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-lg text-nlp-forest">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-base text-nlp-taupe">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-nlp-forest relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-nlp-gold/10 rounded-full blur-3xl" />
        <div className="max-w-[800px] mx-auto relative text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-bg mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Preserve What Made NLP Powerful and Build What Comes Next
          </h2>
          <p className="text-lg text-nlp-cream mb-4 leading-relaxed">
            The future of professional NLP should not require a choice between human skill and technology, intuition and organised intelligence, client transformation and business sustainability, or classical NLP and modern delivery.
          </p>
          <p className="text-xl text-nlp-gold-light font-semibold mb-8">The opportunity is integration.</p>
          <p className="text-lg text-nlp-cream mb-8 leading-relaxed">
            NLP OS gives practitioners and schools the architecture to understand more deeply, personalise the journey, support application, make progress visible, promote more intelligently and build both transactional and recurring income around the skill.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={scrollToSchools} className="gap-2 bg-nlp-gold text-nlp-forest hover:bg-nlp-gold-light rounded-full">
              Talk to Us About Your School
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-nlp-cream/70 mt-12">
            NLP OS<sup className="text-[0.45em] align-super ml-0.5">™</sup> — The Operating System for the Modern NLP Practitioner<sup className="text-[0.45em] align-super ml-0.5">™</sup>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 bg-nlp-bg border-t border-nlp-cream-dark">
        <div className="max-w-[1216px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <NLPLogo className="h-10" />
            </div>
            <nav className="flex flex-wrap gap-6 justify-center">
              <button onClick={scrollToOffers} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Offers
              </button>
              <button onClick={scrollToSchools} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Schools
              </button>
              <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                FAQ
              </button>
              <button onClick={() => navigate('/privacy')} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Privacy
              </button>
              <button onClick={() => navigate('/terms')} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Terms
              </button>
            </nav>
          </div>
          <p className="text-sm text-nlp-taupe mt-6 text-center">
            Copyright 2025 NLP OS, All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
